/* Path: src/components/auth/Login.tsx
 * Memoized useFirebase with useMemo to prevent unnecessary re-renders
 * Updated to resolve merge conflict in Title version number, adopting 2959.OLS 8.0 from HEAD
 * Removed footer rendering, ensuring single footer in PublicLayout.tsx
 * Supports Sun Light Meditation education accessibility and SEO optimization
 * 06/02/25 3:13 PM +07
 * Removed useMemo wrapping around useFirebase to fix React Hook warning
 * Ensured all Hooks are called at the top level
 * Supports Sun Light Meditation education accessibility and SEO optimization
 * 06/02/25 6:02 PM +07
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Button, Card, Checkbox, Form, Input, Typography, Modal, message } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import { useFirebase } from "../dev/Firebase/Firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import LoginMonitor from "./components/LoginMonitor";
import Partners from "./components/Partners";
import MiniOLSUpdates from "./components/MiniOLSUpdates";
import ImgOlslm from "./components/ImgOlslm/ImgOlslm";
import { loginImgURLs } from "./constants/LoginImg";
import { useUserStore } from "../../stores/UserStore";
import { ability } from "../../ability";
import { UserRole } from "../../constants/UserEnums";
import {
  SiGithub,
  SiBlogger,
  SiFacebook,
  SiLinkedin,
  SiYoutube,
  SiX,
} from "@icons-pack/react-simple-icons";
import styled from "styled-components";
import "./login.css";

// Define interface for login form values
interface FormValues {
  username: string;
  password: string;
  remember: boolean;
}

const { Title } = Typography;

// Styled component for the login container with blur effect
const BlurredContainer = styled.div<{ $isBlurred: boolean }>`
  transition: filter 0.3s ease;
  ${(props) => props.$isBlurred && `filter: blur(4px);`}
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  background: #ffe082 !important;
`;

// Styled component for the modal content
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: rgba(17, 17, 17, 0.9);
  border-radius: 8px;
  color: #fff;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
`;

// Styled component for modal text
const ModalText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
  color: #ddd;
  text-align: center;
`;

// Styled component for the modal checkbox wrapper
const ModalCheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

// Styled component for the modal button
const ModalButton = styled(Button)`
  background: #ffaa00;
  border: none;
  color: #0427f4;
  font-weight: 600;
  width: 100%;
  height: 40px;
  &:hover {
    background: #ffcc00 !important;
    color: #0427f4 !important;
  }
`;

// Styled component for modal links
const ModalLink = styled(Link)`
  color: #ffaa00;
  text-decoration: none;
  &:hover {
    color: #ffcc00;
    text-decoration: underline;
  }
`;

const Login: React.FC = () => {
  // Use useFirebase directly at the top level
  const { auth } = useFirebase();

  const [imageUrl, setImageUrl] = useState<string>("OLSLM");
  const [selectedPartnerValue, setSelectedPartnerValue] = useState<string>("w0-1");
  const [isImageOpen, setIsImageOpen] = useState<boolean>(false);
  const [isEstateOpen, setIsEstateOpen] = useState<boolean>(false);
  const [questionList, setQuestionList] = useState<string[]>([]);
  const [activeQW, setActiveQW] = useState<string>("");
  const [titleUrl, setTitleUrl] = useState<string>("");
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isPrivacyModalVisible, setIsPrivacyModalVisible] = useState<boolean>(false);
  const [hasAgreed, setHasAgreed] = useState<boolean>(false);
  const [saveForSixHours, setSaveForSixHours] = useState<boolean>(false);
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const userStore = useUserStore();

  useEffect(() => {
    console.log("Verification: Login component mounted", { isLoggedIn: userStore.isLoggedIn });
    console.log("Checking privacy agreement and timestamp on mount...");
    const agreed = localStorage.getItem("privacyAgreed");
    const timestamp = localStorage.getItem("privacyAgreedTimestamp");
    console.log("Privacy agreed status:", agreed, "Timestamp:", timestamp);

    if (timestamp) {
      const currentTime = Date.now();
      const savedTime = parseInt(timestamp, 10);
      const sixHoursInMs = 6 * 60 * 60 * 1000;
      console.log("Current time:", currentTime, "Saved time:", savedTime, "Diff:", currentTime - savedTime);

      if (!isNaN(savedTime) && currentTime - savedTime < sixHoursInMs) {
        console.log("Valid timestamp found, setting hasAgreed to true and skipping modal");
        setHasAgreed(true);
        setIsPrivacyModalVisible(false);
        return;
      } else {
        console.log("Timestamp expired or invalid, clearing localStorage");
        localStorage.removeItem("privacyAgreedTimestamp");
        localStorage.removeItem("privacyAgreed");
      }
    }

    if (!userStore.isLoggedIn && agreed === "true") {
      console.log("User is not logged in but privacyAgreed is true, resetting...");
      localStorage.removeItem("privacyAgreed");
    }

    const updatedAgreed = localStorage.getItem("privacyAgreed");
    console.log("Updated privacy agreed status:", updatedAgreed);
    if (updatedAgreed === "true") {
      console.log("User has agreed to privacy policy, setting hasAgreed to true");
      setHasAgreed(true);
    } else {
      console.log("User has not agreed, showing privacy modal");
      setIsPrivacyModalVisible(true);
    }
  }, [userStore.isLoggedIn]);

  useEffect(() => {
    console.log("Checking user authentication status - isLoggedIn:", userStore.isLoggedIn);
    if (userStore.isLoggedIn && userStore.user) {
      const isAdmin = userStore.userRole === "ADMIN" || (userStore.userRole && ability.can("access", "Admin panel"));
      console.log("User is logged in, isAdmin:", isAdmin);
      if (isAdmin) {
        console.log("Redirecting admin to /admin/users/all");
        navigate("/admin/users/all");
      } else {
        console.log("Redirecting non-admin user to /verification");
        navigate("/verification");
      }
    }
  }, [userStore.isLoggedIn, userStore.user, userStore.userRole, navigate]);

  const updateWindowDimensions = useCallback(() => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener("resize", updateWindowDimensions);
    return () => window.removeEventListener("resize", updateWindowDimensions);
  }, [updateWindowDimensions]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      setLoading(true);
      setLoginError(null);
      try {
        console.log("Attempting login with email:", values.username);
        const userCredential = await signInWithEmailAndPassword(auth, values.username, values.password);
        message.success("Login successful!");
        console.log("Verification: Login successful", { user: userCredential.user.uid });
      } catch (error: any) {
        console.error("Login failed:", error);
        if (error.code === "auth/network-request-failed") {
          setLoginError("Network error: Unable to connect to the authentication server. Please check your internet connection or try again later.");
          message.error("Network error: Unable to connect to the authentication server. Please check your internet connection or try again later.");
        } else if (error.code === "auth/wrong-password" || error.code === "auth/user-not-found") {
          setLoginError("Invalid email or password. Please try again.");
          message.error("Invalid email or password. Please try again.");
        } else if (error.code === "auth/too-many-requests") {
          setLoginError("Too many login attempts. Please try again later or reset your password.");
          message.error("Too many login attempts. Please try again later or reset your password.");
        } else {
          setLoginError("An unexpected error occurred. Please try again or contact support.");
          message.error("An unexpected error occurred. Please try again or contact support.");
        }
      } finally {
        setLoading(false);
        setIsSubmitting(false);
      }
    },
    [auth, isSubmitting]
  );

  const handlePrivacyAgree = useCallback(() => {
    console.log("Handling privacy agreement - hasAgreed:", hasAgreed, "saveForSixHours:", saveForSixHours);
    if (!hasAgreed) {
      message.error("Please agree to the Privacy Policy.");
      return;
    }
    if (saveForSixHours) {
      const timestamp = Date.now().toString();
      console.log("Saving privacy agreement with timestamp:", timestamp);
      localStorage.setItem("privacyAgreedTimestamp", timestamp);
      localStorage.setItem("privacyAgreed", "true");
    } else {
      console.log("Saving privacy agreement without timestamp");
      localStorage.setItem("privacyAgreed", "true");
    }
    console.log("Privacy agreed, closing modal");
    setIsPrivacyModalVisible(false);
  }, [hasAgreed, saveForSixHours]);

  const onChange = useCallback(
    (value: string) => {
      setImageUrl(value === "982.9.11.3400" ? loginImgURLs[20] : value);
      setTitleUrl(value === "982.9.11.3400" ? "982.9.11.3400" : "");
    },
    []
  );

  const onChangePartners = useCallback((value: string) => {
    setSelectedPartnerValue(value || "w0-1");
  }, []);

  const toggleEstate = useCallback(() => setIsEstateOpen((prev) => !prev), []);

  const onImageClick = useCallback(() => setIsImageOpen((prev) => !prev), []);

  const onArrowClick = useCallback(
    (direction: "up" | "down") => setImageUrl(direction === "up" ? loginImgURLs[0] : loginImgURLs[1]),
    []
  );

  const onQuestionHandler = useCallback((qw: string) => {
    setQuestionList(["0-10", "11-20", "21-30", "31-40", "41-50", "51-60", "61-70", "71-80", "81-90"]);
    setActiveQW(qw);
  }, []);

  const onChangeImageUrlHandler = useCallback((imageUrl: string) => setImageUrl(imageUrl), []);

  const isMobile = dimensions.width < 768;

  return (
    <>
      <Modal
        open={isPrivacyModalVisible}
        footer={null}
        closable={false}
        centered
        width={isMobile ? "90%" : 600}
        styles={{
          body: { background: "transparent", padding: 0 },
          mask: { background: "rgba(0, 0, 0, 0.5)" },
        }}
      >
        <ModalContent>
          <ModalText>
            If you are a human, please agree to our{" "}
            <ModalLink to="/public-use">Public Use Privacy Policy</ModalLink> to view this website. You can also{" "}
            <ModalLink to="/register">register</ModalLink> to save this preference.
          </ModalText>
          <ModalText>
            If you are an AI learning bot, a daily access fee is required. Please{" "}
            <ModalLink to="/contact">contact OLS</ModalLink> for details.
          </ModalText>
          <ModalCheckboxWrapper>
            <Checkbox
              checked={hasAgreed}
              onChange={(e) => setHasAgreed(e.target.checked)}
              style={{ color: "#ddd" }}
              aria-label="Agree to Privacy Policy"
            >
              I am a human and agree to the Privacy Policy
            </Checkbox>
            <Checkbox
              checked={saveForSixHours}
              onChange={(e) => setSaveForSixHours(e.target.checked)}
              style={{ color: "#ddd" }}
              aria-label="Save privacy agreement for 10 hours"
            >
              Save for 10 hours
            </Checkbox>
          </ModalCheckboxWrapper>
          <ModalButton onClick={handlePrivacyAgree} aria-label="Continue after agreeing to Privacy Policy">
            Continue
          </ModalButton>
        </ModalContent>
      </Modal>

      <BlurredContainer $isBlurred={isPrivacyModalVisible} className="login-container">
        <Helmet>
          <title>Login to OLS | Sun Light Meditation Academy</title>
          <meta
            name="description"
            content="Log in to onelightsystem OLS to access Sun Light Meditation education and join the Light Civilization. Join at olsme.com/OLSStudentProspect."
          />
          <meta
            name="keywords"
            content="Sun Light Meditation, OLS meditation education, join OLS, OLS login, Light Civilization"
          />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://www.olsme.com/login" />
          <meta name="author" content="OneLightSystem OLS" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta property="og:title" content="Login to OLS | Sun Light Meditation Academy" />
          <meta
            property="og:description"
            content="Log in to access Sun Light Meditation education and join the Light Civilization. Join at olsme.com/OLSStudentProspect."
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.olsme.com/login" />
          <meta property="og:image" content="https://www.olsme.com/public/logo.png" />
          <meta property="og:image:alt" content="OLS Sun Light Meditation Logo" />
          <meta property="twitter:card" content="summary_large_image" />
          <meta
            property="twitter:title"
            content="Login to OLS | Sun Light Meditation Academy"
          />
          <meta
            property="twitter:description"
            content="Log in to access Sun Light Meditation education and join the Light Civilization. Join at olsme.com/OLSStudentProspect."
          />
          <meta property="twitter:image" content="https://www.olsme.com/public/logo.png" />
          <meta property="twitter:image:alt" content="OLS Sun Light Meditation Logo" />
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "Login to OLS | Sun Light Meditation Academy",
                "url": "https://www.olsme.com/login",
                "description": "Log in to onelightsystem OLS to access Sun Light Meditation education and join the Light Civilization.",
                "publisher": {
                  "@type": "EducationalOrganization",
                  "name": "OneLightSystem OLS",
                  "url": "https://www.olsme.com",
                  "logo": "https://www.olsme.com/public/logo.png",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "2033 Ralston Ave",
                    "addressLocality": "Belmont",
                    "addressRegion": "CA",
                    "postalCode": "94002",
                    "addressCountry": "US"
                  }
                },
                "breadcrumb": {
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {
                      "@type": "ListItem",
                      "position": 1,
                      "name": "Home",
                      "item": "https://www.olsme.com"
                    },
                    {
                      "@type": "ListItem",
                      "position": 2,
                      "name": "Login",
                      "item": "https://www.olsme.com/login"
                    }
                  ]
                }
              }
            `}
          </script>
        </Helmet>

        <div className={`sun-container ${isMobile ? "is-mobile" : ""}`}>
          <div className="sun">
            <div className="center"></div>
            <div className="ray r-1"></div>
            <div className="ray r-2"></div>
            <div className="ray r-3"></div>
            <div className="ray r-4"></div>
            <div className="ray r-5"></div>
            <div className="ray r-6"></div>
            <div className="ray r-7"></div>
            <div className="ray r-8"></div>
            <div style={{ width: "160px", height: "160px", position: "static", visibility: "hidden" }}></div>
          </div>
        </div>

        <div className="img-olslm-wrapper">
          <ImgOlslm
            state={{ imageUrl, isImageOpen, isEstateOpen, images: loginImgURLs, questionList, activeQW, titleUrl, ...dimensions }}
            titleUrl={titleUrl}
            onArrowClick={onArrowClick}
            onChange={onChange}
            onChangeImageUrlHandler={onChangeImageUrlHandler}
            onImageClick={onImageClick}
            onQuestionHandler={onQuestionHandler}
            onChildQuestionHandler={onQuestionHandler}
          />
        </div>
        <span><div style={{ height: "20px" }}></div></span>

        <Card className="login-card">
          {loginError && (
            <div style={{ color: "red", textAlign: "center", marginBottom: 16 }}>
              {loginError}
            </div>
          )}
          <Form
            ref={formRef}
            onFinish={handleSubmit}
            layout="vertical"
            className="login-form"
          >
            <Title level={isMobile ? 4 : 3} style={{ textAlign: "center", marginBottom: 20 }}>
              <a style={{ color: "#0427f4" }} href="https://www.olsme.com/privacy">
                2967.OLS 8.0
                <p>Meditation Education Academy</p>
              </a>
            </Title>

            <Form.Item name="username" rules={[{ required: true, message: "Please input your email!" }]}>
              <Input
                prefix={<UserOutlined />}
                placeholder="Email or verified phone number"
                disabled={loading}
                style={{ height: isMobile ? "40px" : "45px", borderRadius: 6 }}
              />
            </Form.Item>

            <Form.Item name="password" rules={[{ required: true, message: "Please input your passcode!" }]}>
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Passcode"
                disabled={loading}
                style={{ height: isMobile ? "40px" : "45px", borderRadius: 6 }}
              />
            </Form.Item>

            <Form.Item name="remember" valuePropName="checked" initialValue={true}>
              <Checkbox disabled={loading}>Remember me</Checkbox>
            </Form.Item>

            <Form.Item>
              <a className="login-form-forgot" href="/passwordreset">Forgot Passcode?</a>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" className="login-button" loading={loading} disabled={isSubmitting}>
                Login
              </Button>
              <div className="space-between-div">
                <span style={{ textAlign: isMobile ? "center" : "left" }}>
                  Or{" "}
                  <a href="/register">
                    <Button className="register-button" disabled={loading}>Apply to Register</Button>
                  </a>
                </span>
                <a href="mailto:info@olsme.com" style={{ color: "#0427f4", textAlign: isMobile ? "center" : "right" }}>
                  Support
                </a>
              </div>
            </Form.Item>
          </Form>
        </Card>

        <LoginMonitor toggleEstate={toggleEstate} state={{ isEstateOpen }} />
        <span><div style={{ height: "20px" }}></div></span>
        <MiniOLSUpdates />
        <span><div style={{ height: "20px" }}></div></span>
        <Partners
          state={{ selectedPartnerValue, isEstateOpen }}
          onPartnersClick={toggleEstate}
          onChangePartners={onChangePartners}
        />
        <div style={{ height: "20px" }}></div>

        <div className="social-follow-wrapper">
          <a className="social-icon" href="https://github.com/sponsors/onelightsystem" target="_blank" rel="noopener noreferrer">
            <SiGithub className="social-icon-image" fill="#181717" title="GitHub" />
          </a>
          <a className="social-icon" href="https://www.blogger.com/profile/09343545978643567973" target="_blank" rel="noopener noreferrer">
            <SiBlogger className="social-icon-image" fill="#FF5722" title="Blogger" />
          </a>
          <a className="social-icon" href="https://www.facebook.com/onelightsystemols" target="_blank" rel="noopener noreferrer">
            <SiFacebook className="social-icon-image" fill="#0866FF" title="Facebook" />
          </a>
          <a className="social-icon" href="https://www.linkedin.com/company/one-light-system" target="_blank" rel="noopener noreferrer">
            <SiLinkedin className="social-icon-image" fill="#0A66C2" title="LinkedIn" />
          </a>
          <a className="social-icon" href="https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation" target="_blank" rel="noopener noreferrer">
            <SiYoutube className="social-icon-image" fill="#FF0000" title="YouTube" />
          </a>
          <a className="social-icon" href="https://x.com/onelightsystem" target="_blank" rel="noopener noreferrer">
            <SiX className="social-icon-image" fill="#000000" title="X" />
          </a>
        </div>
      </BlurredContainer>
    </>
  );
};

export default Login;